import { useCurrentUser } from './useCurrentUser';

/**
 * Actions that the useCan hook understands.
 *
 * Naming convention: "<resource>.<verb>" or "<area>.<verb>".
 * Add new actions here as features are gated.
 */
export type Action =
  | 'zone.config.edit'      // edit zone settings, checklists, equipment, zoning
  | 'zone.config.view'      // read-only view of management section
  | 'zone.invite'           // create invite links for a zone
  | 'zone.analytics.read'   // read analytics / dashboards
  | 'task.report.submit'    // submit task report (line_worker)
  | 'org.create_zone'       // create a new zone under an org
  | 'global.admin'          // superuser / linza_employee full access
  | 'reports.menu'          // see the Reports sidebar group (excludes line_worker)
  | 'daily_report.manage';  // edit/confirm/cancel/send daily reports + see pending

/** Role codes that can edit zone config (management section). */
const CONFIG_EDIT_ROLES = new Set([
  'zone_admin',
  'zone_manager',
  'org_owner',
  'org_mentor',
  'branch_manager',
  'linza_employee',
  'integrator',
]);

/** Role codes that can read analytics. */
const ANALYTICS_ROLES = new Set([
  'zone_admin',
  'zone_manager',
  'zone_employee',
  'line_worker',
  'org_owner',
  'org_mentor',
  'branch_manager',
  'linza_employee',
  'chain_office',
]);

/** Roles that can see the Reports sidebar group (managers and above, not line_worker). */
const REPORTS_MENU_ROLES = new Set([
  'zone_admin',
  'zone_manager',
  'org_owner',
  'org_mentor',
  'branch_manager',
  'linza_employee',
  'integrator',
  'chain_office',
]);

/** Role codes that can submit task reports. */
const REPORT_ROLES = new Set([
  'zone_employee',
  'line_worker',
  'zone_admin',
  'zone_manager',
  'org_owner',
  'org_mentor',
  'branch_manager',
  'linza_employee',
]);

/** Role codes that can invite members. */
const INVITE_ROLES = new Set([
  'zone_admin',
  'org_owner',
  'org_mentor',
  'branch_manager',
  'linza_employee',
]);

/**
 * Returns true if the current user can perform `action` on *any* zone they
 * belong to. Useful for sidebar/route visibility decisions not scoped to a
 * specific `?zone=` parameter.
 *
 * Returns false while user is loading (deny-by-default, no flash).
 */
export function useCanAnyZone(action: Action): boolean {
  const { user, isLoading } = useCurrentUser();

  if (isLoading || !user) return false;
  if (user.is_superuser) return true;

  const isLinzaEmployee = (user.org_roles ?? []).some(r => r.role === 'linza_employee');
  if (isLinzaEmployee) return true;

  switch (action) {
    case 'zone.config.edit':
      return (
        (user.zone_roles ?? []).some(r => CONFIG_EDIT_ROLES.has(r.role)) ||
        (user.org_roles ?? []).some(r => CONFIG_EDIT_ROLES.has(r.role))
      );
    case 'zone.analytics.read':
      return (
        (user.zone_roles ?? []).some(r => ANALYTICS_ROLES.has(r.role)) ||
        (user.org_roles ?? []).some(r => ANALYTICS_ROLES.has(r.role))
      );
    case 'reports.menu':
      return (
        (user.zone_roles ?? []).some(r => REPORTS_MENU_ROLES.has(r.role)) ||
        (user.org_roles ?? []).some(r => REPORTS_MENU_ROLES.has(r.role))
      );
    default:
      return false;
  }
}

/**
 * Returns true if the current user is allowed to perform `action` within
 * the optional `zoneId` scope.
 *
 * Rules (in priority order):
 * 1. Superusers can do everything.
 * 2. Global roles (linza_employee) can do everything.
 * 3. Org-level roles grant permissions for all zones in that org.
 * 4. Zone-level roles grant permissions for the specific zone.
 *
 * When `zoneId` is omitted the check is org/global only (for actions that
 * don't operate on a specific zone, e.g. org.create_zone).
 *
 * Returns false (not null) while the user is still loading so that the
 * default is "deny" rather than "flash then hide".
 */
export function useCan(action: Action, zoneId?: string | number | null): boolean {
  const { user, isLoading } = useCurrentUser();

  if (isLoading || !user) return false;

  // Superuser bypasses everything.
  if (user.is_superuser) return true;

  const isLinzaSupport = (user.global_roles ?? []).some(r => r.role === 'linza_support');

  // daily_report.manage is intentionally narrow: superuser + linza_support
  // ONLY. Checked before the linza_employee bypass below so that broader
  // global roles do *not* inherit it. The BE gate is the same; FE matches
  // it so we don't render buttons that would 403 on click.
  if (action === 'daily_report.manage') return isLinzaSupport;

  // Check global roles.
  const globalRoles = user.org_roles
    ? user.org_roles.filter(r => r.role === 'linza_employee' || r.role === 'integrator')
    : [];
  const isLinzaEmployee = globalRoles.some(r => r.role === 'linza_employee');
  if (isLinzaEmployee) return true;

  // action-specific check
  switch (action) {
    case 'global.admin':
      return false; // only superuser / linza_employee (handled above)

    case 'zone.config.edit': {
      if (!zoneId) return false;
      const zid = Number(zoneId);
      // Check direct zone membership
      const zoneRole = user.zone_roles?.find(r => r.zone_id === zid);
      if (zoneRole && CONFIG_EDIT_ROLES.has(zoneRole.role)) return true;
      // Check org membership for the zone's org
      if (zoneRole?.org_id != null) {
        const orgRole = user.org_roles?.find(r => r.org_id === zoneRole.org_id);
        if (orgRole && CONFIG_EDIT_ROLES.has(orgRole.role)) return true;
      }
      return false;
    }

    case 'zone.config.view': {
      if (!zoneId) return false;
      const zid = Number(zoneId);
      const zoneRole = user.zone_roles?.find(r => r.zone_id === zid);
      // Everyone with any zone membership can view (even line_worker gets analytics)
      return !!zoneRole;
    }

    case 'zone.invite': {
      if (!zoneId) return false;
      const zid = Number(zoneId);
      const zoneRole = user.zone_roles?.find(r => r.zone_id === zid);
      if (zoneRole && INVITE_ROLES.has(zoneRole.role)) return true;
      if (zoneRole?.org_id != null) {
        const orgRole = user.org_roles?.find(r => r.org_id === zoneRole.org_id);
        if (orgRole && INVITE_ROLES.has(orgRole.role)) return true;
      }
      return false;
    }

    case 'zone.analytics.read': {
      if (!zoneId) return false;
      const zid = Number(zoneId);
      const zoneRole = user.zone_roles?.find(r => r.zone_id === zid);
      if (zoneRole && ANALYTICS_ROLES.has(zoneRole.role)) return true;
      if (zoneRole?.org_id != null) {
        const orgRole = user.org_roles?.find(r => r.org_id === zoneRole.org_id);
        if (orgRole && ANALYTICS_ROLES.has(orgRole.role)) return true;
      }
      return false;
    }

    case 'task.report.submit': {
      if (!zoneId) return false;
      const zid = Number(zoneId);
      const zoneRole = user.zone_roles?.find(r => r.zone_id === zid);
      if (zoneRole && REPORT_ROLES.has(zoneRole.role)) return true;
      if (zoneRole?.org_id != null) {
        const orgRole = user.org_roles?.find(r => r.org_id === zoneRole.org_id);
        if (orgRole && REPORT_ROLES.has(orgRole.role)) return true;
      }
      return false;
    }

    case 'org.create_zone':
      // org_owner and linza_employee can create zones (linza_employee handled above)
      return (user.org_roles ?? []).some(r =>
        r.role === 'org_owner' || r.role === 'org_mentor'
      );

    default:
      return false;
  }
}
